import React from 'react';
import { StatusPill } from '@/components/ui';
import type { StatusMeta } from '@/components/ui';

/**
 * The stages an order row can show in the order-management tabs, and how each
 * one looks. One table for every tab (equity, block, ETF) so a status never
 * drifts between screens.
 *
 * - calculated — `order_status === 2`: sized and ready to submit.
 * - pending    — any other in-flight code, and every processed instruction.
 * - completed  — submitted to the broker; a finished, successful outcome.
 */
export type OrderStage = 'calculated' | 'pending' | 'completed';

export const ORDER_STATUS_META: Record<OrderStage, StatusMeta> = {
    calculated: { label: 'Calculated', tone: 'info' },
    pending: { label: 'Pending', tone: 'warning' },
    completed: { label: 'Completed', tone: 'positive' },
};

/** The stage a calculated-order row is in, from its numeric `order_status`. */
export const calculatedOrderStage = (orderStatus: number): OrderStage =>
    orderStatus === 2 ? 'calculated' : 'pending';

interface OrderStatusPillProps {
    stage: OrderStage;
    /** The text to show — usually the API's `order_status_display`. Defaults to the stage's own label. */
    label?: React.ReactNode;
}

const OrderStatusPill: React.FC<OrderStatusPillProps> = ({ stage, label }) => (
    <StatusPill
        value={stage}
        meta={label === undefined ? ORDER_STATUS_META : { [stage]: { ...ORDER_STATUS_META[stage], label } }}
    />
);

export default OrderStatusPill;
